import { useState, useCallback } from 'react';
import {
  getPendingApprovalDoctors,
  getDoctorDocumentsForReview as apiGetDoctorDocumentsForReview,
  approveDoctorAdmin,
  rejectDoctorAdmin,
  DoctorDTO,
} from '@/services/doctors';
import { toast } from 'sonner';

export const useAdminDoctorApproval = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPendingDoctors = useCallback(async (): Promise<DoctorDTO[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPendingApprovalDoctors();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Erro ao carregar médicos pendentes.');
    } catch (err: any) {
      const msg = err.message || 'Erro desconhecido';
      setError(msg);
      toast.error(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getDoctorDocumentsForReview = useCallback(async (doctorId: string): Promise<any[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGetDoctorDocumentsForReview(doctorId);
      if (response.success && response.data !== undefined) {
        // Retorna os dados diretamente — a rota admin já retorna o formato correto
        // com hasFile, storageProvider, documentType, etc.
        // NÃO normalizar aqui: normalizeDocumentsList remove campos R2-específicos
        // que o DocumentViewerModal precisa para verificar se é legado ou não.
        return Array.isArray(response.data) ? response.data : [];
      }
      throw new Error(response.error || 'Erro ao carregar documentos.');
    } catch (err: any) {
      const msg = err.message || 'Erro desconhecido';
      setError(msg);
      toast.error(msg);
      // Propaga para o modal controlar empty/error sem travar a UI.
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const approveDoctor = useCallback(async (doctorId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await approveDoctorAdmin(doctorId);
      if (response.success) {
        toast.success('Médico aprovado com sucesso!');
        return true;
      }
      throw new Error(response.error || 'Erro ao aprovar médico.');
    } catch (err: any) {
      const msg = err.message || 'Erro desconhecido';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectDoctor = useCallback(async (doctorId: string, rejectionReason: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await rejectDoctorAdmin(doctorId, rejectionReason);
      if (response.success) {
        toast.success('Médico recusado com sucesso.');
        return true;
      }
      throw new Error(response.error || 'Erro ao recusar médico.');
    } catch (err: any) {
      const msg = err.message || 'Erro desconhecido';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getPendingDoctors,
    getDoctorDocumentsForReview,
    approveDoctor,
    rejectDoctor,
    loading,
    error,
  };
};
